import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="direct-uploader"
export default class extends Controller {
  static targets = ["photosContainer", "fileInput"];

  connect() {
    this.previewByFile = new WeakMap();
    this.previewByUploadId = new Map();

    this.handleDirectUploadInitialize =
      this.handleDirectUploadInitialize.bind(this);
    this.handleDirectUploadProgress = this.handleDirectUploadProgress.bind(this);
    this.handleDirectUploadError = this.handleDirectUploadError.bind(this);
    this.handleDirectUploadEnd = this.handleDirectUploadEnd.bind(this);

    this.element.addEventListener(
      "direct-upload:initialize",
      this.handleDirectUploadInitialize,
    );
    this.element.addEventListener(
      "direct-upload:progress",
      this.handleDirectUploadProgress,
    );
    this.element.addEventListener(
      "direct-upload:error",
      this.handleDirectUploadError,
    );
    this.element.addEventListener(
      "direct-upload:end",
      this.handleDirectUploadEnd,
    );
  }

  disconnect() {
    this.element.removeEventListener(
      "direct-upload:initialize",
      this.handleDirectUploadInitialize,
    );
    this.element.removeEventListener(
      "direct-upload:progress",
      this.handleDirectUploadProgress,
    );
    this.element.removeEventListener(
      "direct-upload:error",
      this.handleDirectUploadError,
    );
    this.element.removeEventListener(
      "direct-upload:end",
      this.handleDirectUploadEnd,
    );
  }

  handleFileSelection(event) {
    const files = Array.from(event.target.files || []);

    files.forEach((file) => {
      if (!this.previewByFile.has(file)) {
        const preview = this.buildPreview(file);
        this.previewByFile.set(file, preview);
      }
    });
  }

  handleDirectUploadInitialize(event) {
    const { id, file } = event.detail;
    let preview = this.previewByFile.get(file);

    if (!preview) {
      preview = this.buildPreview(file);
      this.previewByFile.set(file, preview);
    }

    preview.dataset.uploadId = id;
    this.previewByUploadId.set(id, preview);
  }

  handleDirectUploadProgress(event) {
    const { id, progress } = event.detail;
    const preview = this.previewByUploadId.get(id);
    if (!preview) return;

    const progressBar = preview.querySelector(
      "[data-direct-uploader-progress-bar]",
    );
    const progressLabel = preview.querySelector(
      "[data-direct-uploader-progress-label]",
    );

    if (progressBar) {
      progressBar.style.width = `${progress}%`;
    }

    if (progressLabel) {
      progressLabel.textContent = `${Math.round(progress)}%`;
    }
  }

  handleDirectUploadError(event) {
    const { id, error } = event.detail;
    const preview = this.previewByUploadId.get(id);
    if (!preview) return;

    preview.dataset.uploadStatus = "error";
    const status = preview.querySelector("[data-direct-uploader-status]");
    if (status) {
      status.textContent = error || "Upload failed";
    }
  }

  handleDirectUploadEnd(event) {
    const { id } = event.detail;
    const preview = this.previewByUploadId.get(id);
    if (!preview) return;

    preview.dataset.uploadStatus = "complete";

    const progressBar = preview.querySelector(
      "[data-direct-uploader-progress-bar]",
    );
    const progressLabel = preview.querySelector(
      "[data-direct-uploader-progress-label]",
    );
    const status = preview.querySelector("[data-direct-uploader-status]");

    if (progressBar) {
      progressBar.style.width = "100%";
    }

    if (progressLabel) {
      progressLabel.textContent = "100%";
    }

    if (status) {
      status.textContent = "Uploaded";
    }
  }

  buildPreview(file) {
    if (!this.hasPhotosContainerTarget) return null;

    const wrapper = document.createElement("div");
    wrapper.className =
      "relative aspect-square overflow-hidden rounded border border-slate-200 bg-slate-50";
    wrapper.dataset.uploadStatus = "pending";

    const img = document.createElement("img");
    img.className = "h-full w-full object-cover";
    img.alt = file.name;

    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;
    img.onload = () => URL.revokeObjectURL(objectUrl);

    const overlay = document.createElement("div");
    overlay.className = "absolute inset-x-0 bottom-0 bg-white/80 px-2 py-2";

    const progressTrack = document.createElement("div");
    progressTrack.className =
      "h-1 w-full overflow-hidden rounded bg-slate-200";

    const progressBar = document.createElement("div");
    progressBar.className = "h-full w-0 bg-blue-500 transition-all";
    progressBar.dataset.directUploaderProgressBar = "";

    progressTrack.appendChild(progressBar);
    overlay.appendChild(progressTrack);

    wrapper.appendChild(img);
    wrapper.appendChild(overlay);

    this.photosContainerTarget.prepend(wrapper);

    return wrapper;
  }
}
